import { Button, Cell } from '@highlight/ui'
import { COMPLETED_ACTION_DURATION_MS, isUnread, type StagedAction } from '../hooks/use-staged-actions'
import { useStagedActionCallbacks } from '../hooks/use-staged-action-view'
import { useViewActions, useViewOpenHandlers, type ViewOpenTarget } from '../view-atoms'
import type { ViewContexts } from '../view-definitions'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { ActionSource } from './action-source'
import { CellActionsMenu, CellContextMenu } from './cell-actions'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { useLongRunningAction } from '@renderer/hooks/use-long-running-action'
import { memo, useCallback, useMemo } from 'react'

export interface ViewLongRunningActionCellProps {
  action: StagedAction
  sourceLabel?: string
}

export const ViewLongRunningActionCell = memo(function ViewLongRunningActionCell({
  action,
  sourceLabel = 'Home',
}: ViewLongRunningActionCellProps) {
  const { openView } = useViewActions()
  const { markActionAsRead } = useStagedActionCallbacks()
  const openChatProgress = useCallback(
    (target: ViewContexts['chat']) => {
      markActionAsRead(action.id, action)
      openView('chat', target, { sourceLabel })
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'view-progress',
        ...stagedActionEventProperties(action),
        source: 'plan-chat',
      })
    },
    [action, markActionAsRead, openView, sourceLabel],
  )
  const longRunning = useLongRunningAction(action, { openChat: openChatProgress })
  const view = action.view
  const icon = useMemo(() => getActionIcon(action), [action])
  const { isResolved, showViewProgress, viewProgressLabel, elapsed, finalMessage, openProgress } = longRunning

  const isLikelyCompleted = useMemo<boolean>(() => {
    // @ts-ignore
    return action.metadata?.resolveReason?.type === 'warning'
  }, [action.metadata?.resolveReason])

  const openActionView = useCallback(
    (open: ViewOpenTarget) => {
      markActionAsRead(action.id, action)
      openView('action', { actionId: action.id, title: view?.title ?? action.title }, { sourceLabel, ...open })
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'view',
        ...stagedActionEventProperties(action),
        source: 'cell',
      })
    },
    [action, markActionAsRead, openView, sourceLabel, view?.title],
  )
  const { onOpen, onPopout } = useViewOpenHandlers(openActionView)

  if (!view) return null

  return (
    <CellContextMenu items={<CellActionsMenu onPopout={onPopout} />}>
      <div>
        <Cell className="rounded-xl" onClick={onOpen} isPending={!isResolved}>
          <Cell.Header showChevron={false}>
            <ActionIconSlot view={action.view} unread={isUnread(action)}>
              {icon}
            </ActionIconSlot>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Cell.Title className="text-foreground font-normal">{view.title ?? action.title}</Cell.Title>
              <ActionSource action={action} />
            </div>
          </Cell.Header>

          {/* What the run signed off with. The plan itself stays in the drawer —
              once it's done, how it went is the useful thing to show here. */}
          {finalMessage && (
            <Cell.Content>
              <ActionContentPreview content={finalMessage} clamp />
            </Cell.Content>
          )}

          <Cell.Footer>
            <div className="flex w-full items-center justify-between">
              <div className="type-ui-sm flex items-center gap-1.5">
                <span className={`size-2 shrink-0 rounded-full ${isResolved ? 'bg-success' : 'bg-orange-9'}`} />
                <span className={isResolved ? 'text-success-subtle' : 'text-orange-11'}>
                  {isResolved ? (isLikelyCompleted ? 'Likely completed' : 'Completed') : 'In Progress'}
                </span>
                {elapsed && (
                  <>
                    <span className="text-foreground-secondary">·</span>
                    <span className="text-foreground-secondary tabular-nums leading-4">{elapsed}</span>
                  </>
                )}
              </div>

              {showViewProgress && (
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation()
                    openProgress()
                  }}
                >
                  {viewProgressLabel}
                </Button>
              )}
              {!longRunning.showViewProgress && longRunning.alternateLabel && (
                <span className="text-foreground-secondary type-ui-xs">{longRunning.alternateLabel}</span>
              )}
            </div>
          </Cell.Footer>

          {isResolved && action.resolvedAt && (
            <Cell.Progress duration={COMPLETED_ACTION_DURATION_MS} startTime={action.resolvedAt} />
          )}
        </Cell>
      </div>
    </CellContextMenu>
  )
})
